import type { UnitSystem } from '@lift2fit/shared-types';
import { formatWeight, kgToDisplay } from './units';

/**
 * Prescription vocabulary taken from a real personal trainer's programme
 * spreadsheet (a 561-page Google Sheets export, "Mahesh 1.1-1.6 — BLOCK 1.5"):
 *
 *   EXERCISE (movement, variant)  MODIFICATIONS  SETS  REPS  TEMPO
 *   RPE  %   P.LOAD                SUPERSET GROUP  POST SESSION SCORE
 *
 * REPS is not always a number: it is a range ("8-10"), per side ("6/SIDE"),
 * a distance ("1KM", "15M") or strictly time-based. Load is kg, %1RM, or BW.
 * Exercises are grouped by a number into supersets and tri-sets.
 */

export type SetMeasure = 'reps' | 'distance_m' | 'time_s' | 'amrap';

export interface PrescriptionLine {
  /** 1..12 — rows sharing a group number are performed back to back. */
  supersetGroup: number | null;
  prescribedSets: number;
  repMin: number | null;
  repMax: number | null;
  measure: SetMeasure;
  perSide: boolean;
  isBodyweight: boolean;
  tempo: string | null;
  targetRpe: number | null;
  percent1rm: number | null;
  targetWeightKg: number | null;
  targetDistanceM: number | null;
  targetDurationS: number | null;
  restSeconds: number;
}

/** "3 × 6–8", "3 × 8/side", "1 × 1 km", "3 × 15 m", "3 × 10 @ tempo 3-1-1-0" */
export function formatReps(line: Pick<PrescriptionLine, 'measure' | 'repMin' | 'repMax' | 'perSide' | 'targetDistanceM' | 'targetDurationS'>): string {
  const side = line.perSide ? '/side' : '';
  if (line.measure === 'distance_m') {
    const metres = line.targetDistanceM ?? 0;
    return metres >= 1000 ? `${metres / 1000} km${side}` : `${metres} m${side}`;
  }
  if (line.measure === 'time_s') {
    const seconds = line.targetDurationS ?? 0;
    return seconds >= 60 ? `${Math.round((seconds / 60) * 10) / 10} min${side}` : `${seconds}s${side}`;
  }
  if (line.measure === 'amrap') return `AMRAP${side}`;
  if (line.repMin === null && line.repMax === null) return `—${side}`;
  if (line.repMin !== null && line.repMax !== null && line.repMin !== line.repMax) {
    return `${line.repMin}–${line.repMax}${side}`;
  }
  return `${line.repMax ?? line.repMin}${side}`;
}

/** The whole prescription in one line: "3 × 6–8 @ RPE 7 · 40 kg · tempo 3-1-1-0" */
export function formatPrescription(line: PrescriptionLine, unit: UnitSystem): string {
  const parts = [`${line.prescribedSets} × ${formatReps(line)}`];
  if (line.targetRpe !== null) parts.push(`@ RPE ${line.targetRpe}`);
  if (line.isBodyweight) parts.push('BW');
  else if (line.targetWeightKg !== null) parts.push(formatWeight(line.targetWeightKg, unit));
  else if (line.percent1rm !== null) parts.push(`${line.percent1rm}% 1RM`);
  if (line.tempo) parts.push(`tempo ${line.tempo}`);
  if (line.restSeconds > 0 && line.measure === 'reps') parts.push(`rest ${Math.round(line.restSeconds / 15) * 15}s`);
  return parts.join(' · ');
}

/** A logged set in the spreadsheet's own dialect: "20 kg × 8/side", "100 kg × 15 m". */
export function formatLoggedSet(
  set: { weightKg: number; reps: number; measure: SetMeasure; distanceM?: number | null; durationS?: number | null; perSide?: boolean },
  unit: UnitSystem,
): string {
  const side = set.perSide ? '/side' : '';
  if (set.measure === 'distance_m') return `${set.distanceM ?? 0} m${set.perSide ? ' total' : ''}`;
  if (set.measure === 'time_s') {
    const seconds = set.durationS ?? 0;
    return seconds >= 60 ? `${Math.round((seconds / 60) * 10) / 10} min` : `${seconds}s`;
  }
  const load = set.weightKg > 0 ? `${kgToDisplay(set.weightKg, unit)} ${unit === 'imperial' ? 'lb' : 'kg'}` : 'BW';
  const effort = set.measure === 'amrap' ? `AMRAP ${set.reps}` : `${set.reps}${side}`;
  return `${load} × ${effort}`;
}

/** The RPE ⇄ RIR ⇄ %effort ⇄ pain legend, exactly as the trainer's sheet prints it. */
export interface RpeReferenceRow {
  label: string;
  rir: string;
  percentEffort: string;
  effort: string;
  painLevel: string;
  pain: string;
}

export const RPE_REFERENCE: RpeReferenceRow[] = [
  { label: '10', rir: '0 in reserve', percentEffort: '100%', effort: 'Could not do more reps or load', painLevel: '10', pain: 'Unbearable and excruciating' },
  { label: '9.5', rir: '0–1', percentEffort: '95%', effort: 'Could not do more reps, could do slightly more load', painLevel: '9', pain: 'Severe' },
  { label: '9', rir: '1', percentEffort: '90%', effort: 'Could do 1 more repetition', painLevel: '8', pain: 'Intense, dreadful, horrible pain' },
  { label: '8.5', rir: '1–2', percentEffort: '85%', effort: 'Could definitely do 1 more repetition, chance at 2', painLevel: '7', pain: 'Severe, distracting' },
  { label: '8', rir: '2', percentEffort: '80%', effort: 'Could do 2 more repetitions', painLevel: '6', pain: 'Distressing and miserable' },
  { label: '7.5', rir: '2–3', percentEffort: '75%', effort: 'Could definitely do 2 more repetitions, chance at 3', painLevel: '5', pain: 'Distracting' },
  { label: '7', rir: '3', percentEffort: '70%', effort: 'Could do 3 more repetitions', painLevel: '4', pain: 'Nagging and uncomfortable' },
  { label: '5–6', rir: '4–6', percentEffort: '50–60%', effort: 'Could do 4 to 6 more repetitions', painLevel: '3', pain: 'Uncomfortable' },
  { label: '3–4', rir: 'light', percentEffort: '30–40%', effort: 'Light effort', painLevel: '2', pain: 'Mild to annoying' },
  { label: '1–2', rir: 'none left to give', percentEffort: '10–20%', effort: 'Little to no effort', painLevel: '0–1', pain: 'Little to no distress / pain' },
];

export const MOOD_LABELS: Record<number, string> = {
  1: 'Wrecked',
  2: 'Flat',
  3: 'Okay',
  4: 'Good',
  5: 'Excellent',
};
